import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Container, CircularProgress, Alert } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay, faSearch, faRefresh } from '@fortawesome/free-solid-svg-icons';
import challanPrintVarianceService from '../services/challanPrintVarianceService';
import Pagination from '../components/common/Pagination';
import { UI_COLORS } from '../utils/colors.jsx';
import { useTranslation } from 'react-i18next';
import { todayIST } from '../utils/dates';

/**
 * Challans generated on a day but not printed that same day.
 *
 * Pick a date (or a range) and this lists the challans raised in it whose printing
 * happened on some other day — or never happened at all. A challan generated AND
 * printed on the same day is not a variance and is not listed.
 *
 * The date filter is always the GENERATED date: a challan generated on the 28th and
 * printed on the 29th belongs to the 28th, which is the whole point of the report.
 */

const PAGE_LIMIT = 50;

const todayISO = () => {
    try {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
        }).format(new Date());
    } catch (e) {
        return todayIST();
    }
};

/** "Not printed" or how many days late, as a coloured pill. */
const PrintStatus = ({ row }) => {
    const { t } = useTranslation();
    if (!row.printed) {
        return (
            <span className="px-2 py-0.5 rounded-full text-xs font-bold"
                style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
                {t('challanPrintVariance.status.notPrinted')}
            </span>
        );
    }
    const days = row.variance_days;
    // Negative would mean printed BEFORE the generated date — not expected, but shown
    // as-is rather than silently rendered as a late print.
    const late = typeof days === 'number' && days > 0;
    return (
        <span className="px-2 py-0.5 rounded-full text-xs font-bold"
            style={late
                ? { backgroundColor: '#ffedd5', color: '#9a3412' }
                : { backgroundColor: '#f3f4f6', color: '#374151' }}>
            {late
                ? t('challanPrintVariance.status.daysLate', { days })
                : t('challanPrintVariance.status.differentDay')}
        </span>
    );
};

const SourceBadge = ({ source }) => {
    const { t } = useTranslation();
    const sales = source === 'sales';
    return (
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
            style={sales
                ? { backgroundColor: '#dbeafe', color: '#1e40af' }
                : { backgroundColor: '#ede9fe', color: '#5b21b6' }}>
            {sales ? t('challanPrintVariance.source.sales') : t('challanPrintVariance.source.purchase')}
        </span>
    );
};

const ChallanPrintVariancePage = () => {
    const { t } = useTranslation();

    const [fromDate, setFromDate] = useState(todayISO());
    const [toDate, setToDate] = useState(todayISO());
    const [source, setSource] = useState('');       // '' = both
    const [printedFilter, setPrintedFilter] = useState(''); // '' | 'false' | 'true'
    const [search, setSearch] = useState('');

    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = Math.ceil(total / PAGE_LIMIT) || 1;

    // Filters are read through a ref so `load` stays stable and the paging effect below
    // cannot re-fire itself on every keystroke.
    const filterRef = useRef({});
    filterRef.current = { fromDate, toDate, source, printedFilter, search };

    const buildParams = useCallback((page = 1) => {
        const f = filterRef.current;
        const p = { skip: (page - 1) * PAGE_LIMIT, limit: PAGE_LIMIT };
        if (f.fromDate) p.from_date = f.fromDate;
        if (f.toDate) p.to_date = f.toDate;
        if (f.source) p.source = f.source;
        if (f.printedFilter !== '') p.printed = f.printedFilter === 'true';
        if (f.search.trim()) p.search = f.search.trim();
        return p;
    }, []);

    const load = useCallback(async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const params = buildParams(page);
            // The summary ignores the pagination and row-level filters — it describes the
            // whole picked window, so the tiles do not change as you page through.
            const { skip, limit, printed, search: _s, ...summaryParams } = params;
            const [listRes, summaryRes] = await Promise.all([
                challanPrintVarianceService.getList(params),
                challanPrintVarianceService.getSummary(summaryParams),
            ]);

            if (listRes.success) {
                setItems(listRes.data?.items || []);
                setTotal(listRes.data?.total || 0);
            } else if (!listRes.isCanceled) {
                setError(listRes.error);
                setItems([]);
                setTotal(0);
            }
            if (summaryRes.success) setSummary(summaryRes.data);
        } catch (err) {
            setError(err?.message || String(err));
        } finally {
            setLoading(false);
        }
    }, [buildParams]);

    useEffect(() => {
        load(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentPage > 1) load(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const handleApply = () => {
        setCurrentPage(1);
        load(1);
    };

    const totals = summary?.totals;

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            {/* Header */}
            <Box mb={4}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box sx={{ bgcolor: '#0f172a', borderRadius: 2, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                        <FontAwesomeIcon icon={faCalendarDay} />
                    </Box>
                    <Typography variant="h4" fontWeight="800" letterSpacing="-0.5px">
                        {t('challanPrintVariance.title')}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {t('challanPrintVariance.subtitle')}
                </Typography>
            </Box>

            {/* Summary tiles — describe the whole picked window */}
            {totals && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                    {[
                        { label: t('challanPrintVariance.tiles.generated'), value: totals.total_generated ?? 0, color: '#0f172a' },
                        { label: t('challanPrintVariance.tiles.variance'), value: totals.variance ?? 0, color: '#92400e' },
                        { label: t('challanPrintVariance.tiles.notPrinted'), value: totals.not_printed ?? 0, color: '#991b1b' },
                        { label: t('challanPrintVariance.tiles.printedLate'), value: totals.printed_late ?? 0, color: '#9a3412' },
                    ].map(({ label, value, color }) => (
                        <div key={label} className="p-4 rounded-xl border bg-white" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">{label}</p>
                            <p className="text-2xl font-black" style={{ color }}>{value}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3 mb-4 p-4 rounded-xl border bg-white" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('challanPrintVariance.filters.generatedFrom')}</label>
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }} />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('challanPrintVariance.filters.generatedTo')}</label>
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }} />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('challanPrintVariance.filters.type')}</label>
                    <select value={source} onChange={e => setSource(e.target.value)}
                        className="border rounded-lg px-3 py-2 text-sm bg-white" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                        <option value="">{t('challanPrintVariance.filters.allTypes')}</option>
                        <option value="sales">{t('challanPrintVariance.source.sales')}</option>
                        <option value="purchase">{t('challanPrintVariance.source.purchase')}</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('challanPrintVariance.filters.status')}</label>
                    <select value={printedFilter} onChange={e => setPrintedFilter(e.target.value)}
                        className="border rounded-lg px-3 py-2 text-sm bg-white" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                        <option value="">{t('challanPrintVariance.filters.allStatuses')}</option>
                        <option value="false">{t('challanPrintVariance.status.notPrinted')}</option>
                        <option value="true">{t('challanPrintVariance.filters.printedOtherDay')}</option>
                    </select>
                </div>
                <div className="flex-1 w-full min-w-0 sm:min-w-[200px]">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('common.analyticsColumns.search')}</label>
                    <div className="relative">
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                            placeholder={t('challanPrintVariance.searchPlaceholder')}
                            className="w-full border rounded-lg px-3 py-2 text-sm pr-8"
                            style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                            onKeyDown={e => { if (e.key === 'Enter') handleApply(); }}
                        />
                        <FontAwesomeIcon icon={faSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                    </div>
                </div>
                <button onClick={handleApply} disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                    style={{ backgroundColor: '#0f172a' }}>
                    <FontAwesomeIcon icon={loading ? faRefresh : faSearch} className={loading ? 'animate-spin' : ''} />
                    {loading ? t('challanPrintVariance.loading') : t('challanPrintVariance.apply')}
                </button>
            </div>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{typeof error === 'string' ? error : JSON.stringify(error)}</Alert>}

            {/* Per-day breakdown */}
            {summary?.daily?.length > 0 && (
                <div className="rounded-xl border bg-white mb-5 overflow-x-auto" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="text-xs font-bold uppercase tracking-wider text-gray-500"
                                style={{ backgroundColor: '#f9fafb', borderBottom: `2px solid ${UI_COLORS.BORDERS.LIGHT}` }}>
                                <th className="px-4 py-3 text-left">{t('challanPrintVariance.columns.generatedDate')}</th>
                                <th className="px-4 py-3 text-center">{t('challanPrintVariance.tiles.generated')}</th>
                                <th className="px-4 py-3 text-center">{t('challanPrintVariance.tiles.variance')}</th>
                                <th className="px-4 py-3 text-center">{t('challanPrintVariance.tiles.notPrinted')}</th>
                                <th className="px-4 py-3 text-center">{t('challanPrintVariance.tiles.printedLate')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {summary.daily.map((d, idx) => (
                                <tr key={d.date}
                                    style={{ borderTop: `1px solid ${UI_COLORS.BORDERS.LIGHT}`, backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td className="px-4 py-2.5 font-semibold text-gray-900">{d.date}</td>
                                    <td className="px-4 py-2.5 text-center text-gray-700">{d.total_generated}</td>
                                    <td className="px-4 py-2.5 text-center font-bold" style={{ color: '#92400e' }}>{d.variance}</td>
                                    <td className="px-4 py-2.5 text-center" style={{ color: '#991b1b' }}>{d.not_printed}</td>
                                    <td className="px-4 py-2.5 text-center" style={{ color: '#9a3412' }}>{d.printed_late}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Challan list */}
            {loading && !items.length ? (
                <Box display="flex" justifyContent="center" py={10}>
                    <CircularProgress size={36} />
                </Box>
            ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <FontAwesomeIcon icon={faCalendarDay} className="text-4xl mb-3 opacity-30" />
                    <p className="text-sm font-medium">{t('challanPrintVariance.empty')}</p>
                </div>
            ) : (
                <>
                    <div className="rounded-xl border overflow-x-auto" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="text-xs font-bold uppercase tracking-wider text-gray-500"
                                    style={{ backgroundColor: '#f9fafb', borderBottom: `2px solid ${UI_COLORS.BORDERS.LIGHT}` }}>
                                    <th className="px-4 py-3 text-left">{t('common.analyticsColumns.challanNo')}</th>
                                    <th className="px-4 py-3 text-left">{t('challanPrintVariance.columns.type')}</th>
                                    <th className="px-4 py-3 text-left">{t('challanPrintVariance.columns.party')}</th>
                                    <th className="px-4 py-3 text-left">{t('challanPrintVariance.columns.order')}</th>
                                    <th className="px-4 py-3 text-left">{t('challanPrintVariance.columns.generatedDate')}</th>
                                    <th className="px-4 py-3 text-left">{t('challanPrintVariance.columns.printedDate')}</th>
                                    <th className="px-4 py-3 text-center">{t('challanPrintVariance.columns.printStatus')}</th>
                                    <th className="px-4 py-3 text-left">{t('common.analyticsColumns.generatedBy')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row, idx) => (
                                    <tr key={`${row.source}-${row.id}`}
                                        style={{ borderTop: `1px solid ${UI_COLORS.BORDERS.LIGHT}`, backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                        <td className="px-4 py-3 font-mono font-semibold text-gray-900">{row.challan_number || '—'}</td>
                                        <td className="px-4 py-3"><SourceBadge source={row.source} /></td>
                                        <td className="px-4 py-3 text-gray-700">{row.party_name || '—'}</td>
                                        <td className="px-4 py-3 text-gray-700">{row.order_number || '—'}</td>
                                        <td className="px-4 py-3 text-gray-700">{row.generated_date}</td>
                                        <td className="px-4 py-3 text-gray-700">
                                            {row.printed_date || <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="px-4 py-3 text-center"><PrintStatus row={row} /></td>
                                        <td className="px-4 py-3 text-gray-600">{row.generated_by || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={total}
                        onPageChange={setCurrentPage}
                        loading={loading}
                        totalLabel={t('challanPrintVariance.totalLabel')}
                        pageSize={PAGE_LIMIT}
                    />
                </>
            )}
        </Container>
    );
};

export default ChallanPrintVariancePage;
